import { useCallback } from "react";
import { useAuth, useTellescopeMutation } from "@tellescope/tanstack-query";
import type { InboxScheduledItem } from "./use-inbox-scheduled-messages";

export interface UseSendScheduledMessageReturn {
  sendScheduledMessage: (message: InboxScheduledItem) => Promise<void>;
  isLoading: boolean;
}

/**
 * Hook that handles sending scheduled messages by:
 * 1. Deleting the existing scheduled message
 * 2. Sending the message immediately using the appropriate channel mutation
 * 3. Invalidating scheduled message queries to update the UI
 */
export const useSendScheduledMessage = (): UseSendScheduledMessageReturn => {
  const { currentUser } = useAuth();

  const updateSmsMutation = useTellescopeMutation({
    model: "sms_messages",
    method: "updateOne",
  });
  const updateChatMutation = useTellescopeMutation({
    model: "chats",
    method: "updateOne",
  });
  const updateEmailMutation = useTellescopeMutation({
    model: "emails",
    method: "updateOne",
  });

  const sendScheduledMessage = useCallback(
    async (message: InboxScheduledItem) => {
      switch (message.channel) {
        case "SMS": {
          await updateSmsMutation.mutateAsync({
            id: message.id,
            sendAt: new Date(),
          });

          break;
        }

        case "Email": {
          await updateEmailMutation.mutateAsync({
            id: message.id,
            sendAt: new Date(),
          });
          break;
        }

        case "Chat": {
          if (!currentUser.id) {
            throw new Error("Current user is required to send chat messages");
          }

          await updateChatMutation.mutateAsync({
            id: message.id,
            sendAt: new Date(),
            timestamp: new Date(),
          });
          break;
        }

        default: {
          const exhaustiveCheck: never = message;
          throw new Error(`Unhandled message channel: ${(exhaustiveCheck as InboxScheduledItem).channel}`);
        }
      }
    },
    [updateSmsMutation, updateEmailMutation, currentUser.id, updateChatMutation]
  );

  const isLoading = updateChatMutation.isPending || updateSmsMutation.isPending || updateEmailMutation.isPending;

  return {
    sendScheduledMessage,
    isLoading,
  };
};
